import classNames from 'classnames';
import type { ReactElement, Ref, RefObject } from 'react';
import { useRef } from 'react';
import styles from './PageHeader.module.scss';
import { setupTransitionInTimeline } from './PageHeader.transitions';
import type { PageHeaderProps, PageHeaderRef } from './PageHeader.data';
import { useScrollTransition } from 'util/react-transition-component';
import type { ImageBlok, Video as VideoType } from '../../../util/storyblok/types/global.types';
import Heading from '../../atoms/heading/Heading';
import Text from '../../atoms/text/Text';
import Image from '../../atoms/image/Image';
import Video from '../../atoms/video/Video';

function PageHeader({
  blok,
  canTransitionIn,
  ref,
  ...props
}: PageHeaderProps & { ref?: Ref<PageHeaderRef> }): ReactElement {
  const localRef = useRef(null);
  const elementRef = (ref || localRef) as RefObject<HTMLDivElement | null>;
  const titleRef = useRef<HTMLHeadingElement>(null);
  const descriptionRef = useRef<HTMLElement>(null);
  const categoryRef = useRef<HTMLHeadingElement>(null);
  const mediaWrapperRef = useRef<HTMLDivElement>(null);
  const mediaInnerHolderRef = useRef<HTMLDivElement>(null);

  useScrollTransition({
    scroll: {
      canTransitionIn,
      trigger: elementRef,
    },
    setupOptions: () => ({
      refs: {
        elementRef,
        titleRef,
        categoryRef,
        descriptionRef,
        mediaWrapperRef,
        mediaInnerHolderRef,
      },
      setupTransitionInTimeline,
    }),
  });

  let image;
  if (blok?.key_image && blok.key_image.length > 0) {
    image = blok.key_image[0] as ImageBlok;
  }

  let video;
  if (blok?.key_video && blok.key_video.length > 0) {
    video = blok.key_video[0] as VideoType;
  }

  return (
    <div ref={elementRef} className={classNames(styles.pageHeader, props.className)}>
      <div className={classNames(styles.header, 'grid-gutter')}>
        <Heading
          ref={categoryRef}
          disableTriggerInOnScroll
          as="h2"
          size="label-large"
          transitionInType="splitLineRotateIn"
          className={classNames(styles.category)}
        >
          {blok?.category}
        </Heading>

        <Heading
          ref={titleRef}
          disableTriggerInOnScroll
          as="h1"
          size="heading-02"
          transitionInType="splitCharsSlideInUp"
          className={classNames(styles.title)}
        >
          {blok?.title}
        </Heading>

        <Text
          ref={descriptionRef}
          disableTriggerInOnScroll
          as="p"
          size="body-large"
          transitionInType="splitLineRotateIn"
          className={classNames(styles.description)}
        >
          {blok?.short_description}
        </Text>
      </div>

      {(image?.image_landscape || image?.image_portrait || video) && (
        <div ref={mediaWrapperRef} className={classNames(styles.mediaWrapper)}>
          <div className={classNames(styles.mediaHolder)}>
            <div
              ref={mediaInnerHolderRef}
              className={classNames(styles.mediaInnerHolder, 'abs-fill')}
            >
              {(image?.image_landscape || image?.image_portrait) && !video && (
                <Image
                  src={image.image_landscape || image.image_portrait}
                  srcDesktop={image.image_landscape || image.image_portrait}
                  className={classNames(styles.image, 'abs-fill')}
                  innerTransition={
                    blok.no_inner_parallax
                      ? undefined
                      : {
                          type: 'scaleUp',
                        }
                  }
                  layout="fill"
                  alt={image?.alt_text}
                />
              )}

              {video && (
                <Video
                  blok={video}
                  objectFit="cover"
                  controls
                  playButton
                  innerTransition={
                    blok.no_inner_parallax
                      ? undefined
                      : {
                          type: 'scaleUp',
                        }
                  }
                  muted={false}
                  className={classNames(styles.video, 'abs-fill')}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

PageHeader.displayName = 'PageHeader';

export default PageHeader;
